// The customer's stored payment cards, as kumpula shows them.
//
// users/{uid}/cards/{cardid} holds only masked fields - { card: { type, partial_pan, expire_month,
// expire_year, funding }, provider, timestamp } - written by falcon cardstoring.js. The token itself
// is server-side (cardtokens/{cardid}) and never reaches the client.

const pad2 = (n) => String(n).padStart(2, '0')

const expiry = (card) => {
  const c = (card && card.card) || {}
  const month = parseInt(c.expire_month, 10)
  const year = parseInt(c.expire_year, 10)
  if (!month || !year) return null
  return { month, year: year < 100 ? 2000 + year : year }
}

// A card is valid through the last day of its expiry month.
export const isCardExpired = (card, now) => {
  const e = expiry(card)
  if (!e) return false
  const today = now ? new Date(now) : new Date()
  const y = today.getFullYear()
  const m = today.getMonth() + 1
  return y > e.year || (y === e.year && m > e.month)
}

// Expiry as 'mm/yy', '' when unknown.
export const cardExpiryLabel = (card) => {
  const e = expiry(card)
  return e ? pad2(e.month) + '/' + String(e.year).slice(-2) : ''
}

// The brand logo for a stored card, from its Paytrail `card.type` ('Visa', 'MasterCard',
// 'American Express', ...). Logos are Paytrail's own payment-method SVGs, copied unchanged into
// public/imgs/cards; an unknown brand falls back to the Paytrail logo the profile always showed.
// Returns a path relative to PUBLIC_URL.
export const cardBrandIcon = (card) => {
  const type = String((card && card.card && card.card.type) || (card && card.type) || '').toLowerCase()
  if (type.includes('visa')) return '/imgs/cards/visa.svg'
  if (type.includes('master')) return '/imgs/cards/mastercard.svg'
  if (type.includes('amex') || type.includes('american express')) return '/imgs/cards/amex.svg'
  return '/imgs/' + ((card && card.provider) || 'paytrail') + '.svg'
}

// True when the card's logo already names its brand, so the brand need not be written out too.
export const hasBrandLogo = (card) => cardBrandIcon(card).startsWith('/imgs/cards/')

// The card list for a picker: newest first, each with what the UI shows.
// `cards` is the Firestore map (or array) of users/{uid}/cards.
export const cardList = (cards, now) => {
  const list = Array.isArray(cards)
    ? cards
    : Object.keys(cards || {}).map((id) => (cards[id] ? { ...cards[id], id: cards[id].id || id } : null))
  return list
    .filter((c) => c && c.id && c.card)
    .map((c) => ({
      id: c.id,
      type: c.card.type || '',
      label: '**** ' + (c.card.partial_pan || ''),
      expires: cardExpiryLabel(c),
      expired: isCardExpired(c, now),
      provider: c.provider || 'paytrail',
      icon: cardBrandIcon(c),
      brandShown: hasBrandLogo(c),
      timestamp: c.timestamp || '',
    }))
    .sort((a, b) => String(b.timestamp).localeCompare(String(a.timestamp)))
}

// Where to go after /addcard. Adding a card leaves the app for Paytrail's form and comes back with a
// full page load, so anything in redux (a membership being bought) is gone; sessionStorage is what
// survives. A screen that sends the customer to add a card sets this; AddCardResult takes it.
const RETURN_KEY = 'kumpula.addcard.return'

export const setAddCardReturn = (path) => {
  try {
    if (path) window.sessionStorage.setItem(RETURN_KEY, path)
    else window.sessionStorage.removeItem(RETURN_KEY)
  } catch (e) {
    // storage blocked (private mode): the customer lands on the profile, as before
  }
}

// Read once: the return path, or null. Cleared so a later, unrelated card add goes to the profile.
export const takeAddCardReturn = () => {
  try {
    const path = window.sessionStorage.getItem(RETURN_KEY)
    window.sessionStorage.removeItem(RETURN_KEY)
    return path || null
  } catch (e) {
    return null
  }
}

// The card to preselect: the newest one that has not expired.
export const defaultCardId = (list) => {
  const valid = (list || []).find((c) => !c.expired)
  return valid ? valid.id : null
}
